import axios from 'axios'

/**
 * Build a readable, single-string summary of an API error's status + response
 * body for logging.
 *
 * Why this exists: Sentry's captureConsoleIntegration joins console.error
 * arguments into the captured event message. A raw object renders as
 * "[object Object]" and a missing field as "undefined", producing Sentry issues
 * that can't be triaged (e.g. "MS Graph API Error: 400 [object Object]"). Passing
 * the status and body through this helper keeps both legible in Sentry and the
 * local console.
 */
export function formatApiErrorDetails(status: number | undefined, data: unknown): string {
  return `status=${status ?? 'none'} body=${serializeErrorBody(data)}`
}

/**
 * Whether an API failure is a transient network/connectivity condition rather
 * than a real backend response. These carry no HTTP status (the request never
 * reached the server: offline, DNS failure, timeout, sleep/wake) and are not
 * actionable in app code, so callers should log them at `warn` — below Sentry's
 * captureConsoleIntegration `error` threshold — instead of `error`. Prevents
 * offline calendar polling from flooding Sentry (MOBILE-EV/EW/DP/ET/EP/EN/DV).
 */
export function isTransientNetworkError(status: number | undefined, message?: string): boolean {
  if (status !== undefined) return false
  return /network error|timeout|ECONNABORTED|ERR_NETWORK|Failed to fetch/i.test(message ?? '')
}

/**
 * Log a caught API error at the right level for Sentry, without leaking PII.
 *
 * captureConsoleIntegration ships every `console.error` to Sentry, and a raw
 * AxiosError carries its request config (auth header + body = third-party PII),
 * so this helper logs only `formatApiErrorDetails(status, body)` — never the raw
 * error. Expected/handled failures (a transient network drop, or any status in
 * `expectedStatuses`) log at `warn`, below the capture threshold; anything else
 * stays `error` so real problems still report.
 *
 * NOTE on the warn-vs-error split: this is NOT the same behaviour as the search
 * siblings (useTrayTitle.ts, useEventSearch.ts, useNoteSearch.ts), which log
 * EVERY caught failure at `warn` because a search-load failure is never
 * actionable. This helper is for STORE loads, where an unexpected status (a 500,
 * an unforeseen 4xx) is real signal we want in Sentry — so it escalates those to
 * `error` and only downgrades the expected ones. The two patterns are
 * intentionally distinct; the shared piece both reuse is `formatApiErrorDetails`,
 * which is the one PII rule (response body + status only, never request config).
 */
export function logHandledApiError(
  context: string,
  error: unknown,
  expectedStatuses: number[] = [401, 403]
): void {
  const isAxios = axios.isAxiosError(error)
  const status = isAxios ? error.response?.status : undefined
  const details = isAxios ? formatApiErrorDetails(status, error.response?.data) : String(error)
  const message = isAxios ? error.message : undefined
  if (
    isTransientNetworkError(status, message) ||
    (status !== undefined && expectedStatuses.includes(status))
  ) {
    console.warn(context, details)
  } else {
    console.error(context, details)
  }
}

/**
 * Whether a Sentry `originalException` is a transient axios network failure that
 * should be dropped in `beforeSend`. Before this, the transient-axios check lived
 * only in mobile.ts's beforeSend; the desktop renderer and tray did not drop these
 * at all (the desktop `net::ERR_*` ignoreErrors regex is a separate, pre-existing
 * mechanism). This is now the shared home for the check, called from all three
 * renderer inits — so adding it to the desktop renderer and tray is a deliberate
 * new drop there, gated by `requireOffline` (see below).
 *
 * The `name === 'AxiosError'` gate is load-bearing, not cosmetic: app code
 * throws plain Errors whose text also matches the transient regex (google.ts /
 * microsoft.ts throw "Failed to fetch events from the … API", Vite throws
 * "Failed to fetch dynamically imported module" on a stale deploy). Those are
 * real signal and must keep reporting, so only errors axios itself constructed
 * are eligible. A backend response (status present, incl. 5xx) is likewise kept.
 *
 * `requireOffline`: when true, only drop while `navigator.onLine === false`.
 * Desktop runs in real Chromium where `navigator.onLine` is reliable, so a
 * statusless "Network Error" that happens while genuinely online is a real
 * backend/DNS/LB outage worth reporting — keep the gate there. Mobile WebViews
 * report `onLine: true` on captive portals / wifi-without-internet, so the gate
 * is useless there and is left off (the bulk of mobile's transient noise leaked
 * through precisely because of that).
 */
export function isTransientAxiosError(
  error: unknown,
  { requireOffline = false }: { requireOffline?: boolean } = {}
): boolean {
  if ((error as { name?: string } | null)?.name !== 'AxiosError') return false
  if (requireOffline && navigator.onLine !== false) return false
  const status =
    (error as { response?: { status?: number }; status?: number }).response?.status ??
    (error as { status?: number }).status
  const message = error instanceof Error ? error.message : undefined
  return isTransientNetworkError(status, message)
}

function serializeErrorBody(data: unknown): string {
  if (data === undefined) return 'undefined'
  if (data === null) return 'null'
  if (typeof data === 'string') return data
  if (data instanceof Error) return `${data.name}: ${data.message}`
  let serialized: string
  try {
    serialized = JSON.stringify(data)
  } catch {
    serialized = String(data)
  }
  // Cap to keep Sentry messages (and the console) manageable.
  return serialized.length > 500 ? `${serialized.slice(0, 500)}…` : serialized
}
